'use client';

import clsx from 'clsx';
import { Children, useMemo, useState, useTransition } from 'react';

import { usePrevState, useUpdate } from '@/shared/hooks';

import { TabsList } from './tabs-list';

type TabsProps = React.PropsWithChildren & {
	onChange?: (name: string) => void;
};

export const Tabs = ({ children, ...props }: TabsProps) => {
	const childs = useMemo(
		() => Children.toArray(children).filter((c: any) => c?.props?.name),
		[children],
	);

	const [, startTransition] = useTransition();
	const prevChilds = usePrevState(childs);

	const [activeTab, setActiveTab] = useState(() => {
		const active = childs.reduceRight<any>(
			(a, c: any) => (!a || c.props.active ? c : a),
			childs[0] || null,
		);
		return active?.props.name || null;
	});

	const onChange = (name: string) => {
		startTransition(() => {
			setActiveTab(name);
			props.onChange?.(name);
		});
	};

	useUpdate(() => {
		if (!prevChilds) {
			return;
		}

		const diff = childs.find((p: any, k) => {
			return p.props.tabKey !== (prevChilds[k] as any)?.props.tabKey;
		}) as any;

		if (diff) {
			startTransition(() => setActiveTab(diff?.props.name));
		}
	}, [prevChilds, childs]);

	return (
		<>
			<div className="sticky top-0 z-10 flex snap-mandatory overflow-x-auto bg-white shadow-[inset_0_-1px_0_#D1D5DB]">
				<TabsList active={activeTab} options={childs} onChange={onChange} />
			</div>
			{childs
				.filter(
					(c: any) => c?.props.name === activeTab || c.props.name === 'video',
				)
				.map((c: any) => {
					return (
						<div
							key={c?.props.name}
							className={clsx(
								{
									hidden: c?.props.name !== activeTab,
								},
								'relative flex min-h-[calc(100%-37px-77px)] flex-1 flex-col items-stretch md:min-h-[calc(100%-45px)]',
							)}
						>
							{c?.props.children}
						</div>
					);
				})}
		</>
	);
};
